import React, { useMemo } from 'react'
import { TextField } from '@common/material'
import { Autocomplete } from '@common/material/Autocomplete'
import { Box, Text } from '@fundamentals'
import { Section } from '@common/Section'
import { useProjectUsers } from '@core/react-query/features/projects/project/hooks/useProjectUsers'
import { AutocompleteProps, Chip } from '@mui/material'
import { localizedStrings } from '@core/strings'
import { ProjectUser } from '@core/entities/user'
import { useGetProfile } from '@core/react-query/features/profile'
import { FieldValues, UseFormSetValue } from 'react-hook-form'
import { PermissionEntityType } from '@core/entities/Groups/GroupsPermissions'

type ProjectUserProps<
  Multiple extends boolean,
  DisabledClearable extends boolean,
> = Partial<AutocompleteProps<number, Multiple, DisabledClearable, false>> & {
  projectId: number
  permitHolder?: ProjectUser
  setPermitHolder?: UseFormSetValue<FieldValues>
  onChange?: (userId: number | Array<number>) => void
  readOnly?: boolean
  error?: boolean
  multiple?: Multiple
  disabledClearable?: DisabledClearable
  helperText?: string
  allowSelf?: boolean
  referenceObjectType?: PermissionEntityType
  referenceObjectId?: number
  label?: string
  required?: boolean
  initialUsers?: ProjectUser[]
  canRemoveInitialUsers?: boolean
  companyIds?: string[]
}

const ProjectUserPicker: <
  Multiple extends boolean,
  DisabledClearable extends boolean,
>(
  props: ProjectUserProps<Multiple, DisabledClearable>,
) => JSX.Element = ({
  projectId,
  permitHolder,
  setPermitHolder,
  readOnly,
  onChange,
  value,
  error,
  helperText,
  multiple,
  allowSelf,
  referenceObjectId,
  referenceObjectType,
  label,
  required,
  initialUsers,
  canRemoveInitialUsers = true,
  companyIds,
  ...props
}) => {
  const [searchTerm, setSearchTerm] = React.useState('')
  const { data: projectUsers } = useProjectUsers(
    {
      projectId,
      term: searchTerm,
      memberStatus: 'ACTIVE',
      referenceObjectId: referenceObjectId,
      referenceObjectType: referenceObjectType,
      companyIds,
    },
    { keepPreviousData: true, refetchOnMount: false },
  )
  const { data: profile } = useGetProfile()

  const handleDelete = (userId: number) => {
    onChange((value as number[])?.filter((v) => v !== userId))
  }

  const options = useMemo(() => {
    const projectUsersMap = new Map<number, ProjectUser>()

    if (permitHolder) projectUsersMap.set(permitHolder.id, permitHolder)

    initialUsers?.forEach((user) => {
      projectUsersMap.set(user.id, user)
    })

    return (
      projectUsers?.pages?.[0]?.content.reduce((acc, user) => {
        if (!allowSelf && profile?.id && profile?.id === user.id) {
          return acc
        }

        acc.set(user.id, user)

        return acc
      }, projectUsersMap) || projectUsersMap
    )
  }, [projectUsers, initialUsers, permitHolder, allowSelf, profile?.id])

  if (readOnly) {
    let projectUser = projectUsers?.pages?.[0]?.content.find(
      (user) => user.id === value,
    )
    if (!projectUser && permitHolder?.id === value) {
      projectUser = permitHolder
    }

    return (
      <Section sx={props?.sx} label={localizedStrings.assignment}>
        <Text>{projectUser?.getFullName()}</Text>
        <Text color='grey'>{projectUser?.email}</Text>
      </Section>
    )
  }

  return (
    <Autocomplete
      value={value}
      multiple={multiple}
      options={Array.from(options.keys()).filter((option) => {
        return (
          canRemoveInitialUsers ||
          !initialUsers?.some((user) => user.id === option)
        )
      })}
      isOptionEqualToValue={(option, value) => option === value}
      getOptionLabel={(option) => options.get(option)?.getFullName()}
      filterOptions={(x) => x}
      renderOption={(props, optionValue) => {
        const option = options.get(optionValue)
        return (
          <li
            data-test={`select-user-dropdown-option-${props['data-option-index']}`}
            {...props}
            key={option?.id}
          >
            <Box>
              <Text>{option?.getFullName()}</Text>
              <Text fontSize={15} color='grey'>
                {option?.email}
              </Text>
            </Box>
          </li>
        )
      }}
      onInputChange={(_, newInputValue, inputChangeReason) => {
        if (inputChangeReason === 'input') setSearchTerm(newInputValue)
      }}
      onChange={(_, newValue) => {
        if (!multiple) {
          setPermitHolder?.('permitHolder', options.get(newValue as number))
        }

        const isRemovingInitialUsers = initialUsers?.some(
          (user) => Array.isArray(newValue) && !newValue.includes(user.id),
        )

        if (isRemovingInitialUsers && !canRemoveInitialUsers) {
          return
        }

        onChange(newValue as number[])
      }}
      renderTags={(value, getTagProps) => {
        return value.map((option) => {
          const props = getTagProps({ index: option })
          const isInitialUser = initialUsers?.some((user) => user.id === option)
          const disabled = isInitialUser && !canRemoveInitialUsers
          return (
            <Chip
              key={option}
              label={options.get(option)?.getFullName()}
              {...props}
              size='small'
              sx={{
                opacity: disabled ? 0.5 : 1,
              }}
              onDelete={disabled ? undefined : () => handleDelete(option)}
            />
          )
        })
      }}
      renderInput={(params) => {
        return (
          <TextField
            label={label || localizedStrings.assignTo}
            defaultValue={value}
            data-test='select-user-dropdown-btn'
            error={error}
            helperText={helperText}
            FormHelperTextProps={{ error, sx: { pt: 0.5, pb: 2 } }}
            required={required}
            {...params}
          />
        )
      }}
      disableClearable
      {...props}
    />
  )
}

export { ProjectUserPicker }
